import e from "express";
import { imageUtils } from "./IconUtils";

export const pricingPlanOptions = [
  "account",
  "base",
  "bundle",
  "extra",
  "feature",
  "capacity",
] as const;

export type pricingPlanTypes = (typeof pricingPlanOptions)[number];

export enum PricingPlanTypeEnum {
  ACCOUNT = "account",
  BASE = "base",
  BUNDLE = "bundle",
  EXTRA = "extra",
  FEATURE = "feature",
  CAPACITY = "capacity",
}

export interface pricingPlanDto {
  currency: string;
  features: Array<string>;
  key: string;
  level: number;
  metadata: {
    [key: string]: string;
  };
  lookupKey: string;
  price: number;
  type: pricingPlanTypes;
}

export declare interface orgPackageDataType {
  id: number;
  title: string;
  lookupKey: string | null;
  unlimitedStaff: boolean;
  unlimitedTraining: boolean;
  contact: boolean;
  isSelected: boolean;
  imageUrl: string;
  heading: string;
  description1: string;
  description2?: string;
  isEnterprice?: boolean;
}

const getFeaturesByBundle = (key: string): Array<string> => {
  switch (key) {
    case "business_essentials":
      return ["timesheet"];

    case "information_induction":
      return ["documents.view-sign", "documents.manage", "inductions"];

    case "work_assessment":
      return ["progress", "variations", "defects"];

    case "custom_swms_jsa":
      return ["swms", "jsa"];

    case "hazard_management":
      return [
        "notifications",
        "notifications.create",
        "notifications.make-safe",
        "notifications.repair-work",
        "documents",
      ];

    case "safety_compliance":
      return [
        "pst",
        "pssc",
        "notifications",
        "notifications.create",
        "notifications.make-safe",
        "notifications.repair-work",
        "swms",
        "swms.create",
        "swms.view",
        "swms.send",
        "jsa",
        "audits",
      ];

    case "audit_tool":
      return ["audits", "incident"];

    case "financial_tools":
      return ["purchases"];

    case "safework_essentials":
    case "safe_work_pack":
      return ["audits", "incidents", "documents"];

    case "swms_only":
      return ["swms", "jsa"];

    case "complete_construction":
    case "swms_safe_work_pack":
      return [
        "courses_inductions",
        "audits",
        "pst",
        "pssc",
        "incidents",
        "documents",
        "swms",
        "jsa",
      ];

    case "sb_pro_pack":
    case "safety_compliance_bm_pack":
      return [
        "courses_inductions",
        "audits",
        "progress_reports",
        "pst",
        "pssc",
        "variations",
        "defects",
        "incidents",
        "documents",
        "swms",
        "jsa",
        "purchases",
        "audit_sharing",
      ];

    default:
      return [];
  }
};

export const removeDuplicateFeatures = (
  features: Array<string>,
  bundle: Array<string>,
) => {
  const finalFeatureList = bundle.map((e) => getFeaturesByBundle(e)).flat();
  const uniqueFeatures = [...new Set(finalFeatureList)];
  const removedFeatures = features.filter((e) => !uniqueFeatures.includes(e));
  return removedFeatures;
};

export const orgPackageData: orgPackageDataType[] = [
  {
    id: 1,
    title: "Small Business Pack 1",
    lookupKey: "1-base",
    unlimitedStaff: false,
    unlimitedTraining: false,
    contact: false,
    imageUrl: imageUtils.smallBusiness1,
    isSelected: false,
    heading: "Account Size",
    description1: "Fixed number of Business Management and Staff accounts.",
    description2: "A cost effective solution for Small Businesses.",
  },
  {
    id: 2,
    title: "Small Business Pack 2",
    lookupKey: "2-base",
    unlimitedStaff: false,
    unlimitedTraining: false,
    contact: false,
    imageUrl: imageUtils.smallBusiness2,
    isSelected: false,
    heading: "Add Accounts",
    description1: "Add more Business Management or Staff Accounts.",
    description2: "A cost effective solution for Small Businesses.",
  },
  {
    id: 3,
    title: "Business Pack 3",
    lookupKey: "3-base",
    unlimitedStaff: false,
    unlimitedTraining: false,
    contact: false,
    imageUrl: imageUtils.businessPack,
    isSelected: false,
    heading: "Add Accounts",
    description1:
      "Add more Business Management or Staff after making all other selections.",
  },
  {
    id: 4,
    title: "Medium Business Pack",
    lookupKey: "4-base",
    unlimitedStaff: false,
    unlimitedTraining: false,
    contact: false,
    imageUrl: imageUtils.mediumBusiness,
    isSelected: false,
    heading: "Add Accounts",
    description1:
      "Add more Business Management or Staff Accounts after making all other selections.",
  }
];

const marketingPricingAssetBase = "/assets/images/pricing";

export type MarketingBundleContent = {
  imageUrl: string;
  marketingTitle: string;
  marketingSubtitle: string;
  marketingTagline: string;
  marketingBullets: string[];
  marketingBadge?: string;
  marketingIncludesFrom?: string;
  marketingHighlight?: boolean;
  marketingButtonTone?: "accent" | "primary";
};

export type MarketingOrganisationContent = {
  imageUrl: string;
  marketingTitle: string;
  marketingOriginalPrice?: string;
};

export const marketingBundleContent: Record<string, MarketingBundleContent> = {
  swms_only: {
    imageUrl: "/assets/images/marketing/hero-tradie.jpg",
    marketingTitle: "SWMS Only",
    marketingSubtitle: "The easiest way to get site-ready",
    marketingTagline:
      "Simple SWMS compliance for teams that just need fast site-ready documents without the full compliance stack.",
    marketingBullets: [
      "Unlimited SWMS: Build site-specific SWMS possible in 90 seconds.",
      "Digital Sign-Off: Instant on-site signing for your crew and contractors right from their phones.",
      "Instant Sharing: One-click sending to clients to prove you're site-ready.",
    ],
    marketingButtonTone: "accent",
  },
  safework_essentials: {
    imageUrl: `${marketingPricingAssetBase}/pricing-essentials.jpg`,
    marketingTitle: "Safework Essentials Pack",
    marketingSubtitle: "Safety essentials for any industry",
    marketingTagline:
      "Safety 'essentials' for any industry including onsite and management tools to operate safe sites, protect your people and your business.",
    marketingBullets: [
      "Unlimited projects and jobs",
      "Monthly subscription – no lock-in contracts",
      "App for all accounts & Web Portal for Managers",
      "Expert local phone support",
    ],
    marketingButtonTone: "accent",
  },
  complete_construction: {
    imageUrl: `${marketingPricingAssetBase}/pricing-construction.jpg`,
    marketingTitle: "Complete Construction Pack",
    marketingSubtitle: "Built for construction compliance",
    marketingTagline:
      "A complete compliance system for the construction industry including unlimited Projects — no limits on SWMS or any other report.",
    marketingBullets: [
      "Simple SWMS builder — easy on site or in office",
      "Straight forward, affordable compliance",
      "Unlimited projects and jobs",
      "Expert local phone support",
    ],
    marketingBadge: "Most Popular",
    marketingIncludesFrom: "Safework Essentials Pack",
    marketingHighlight: true,
    marketingButtonTone: "primary",
  },
  sb_pro_pack: {
    imageUrl: `${marketingPricingAssetBase}/pricing-safety.jpg`,
    marketingTitle: "Safety & Business Pro Pack",
    marketingSubtitle: "All features for your whole operation",
    marketingTagline:
      "All features included and local phone and onsite support for your business. Add frequent contractors at no cost.",
    marketingBullets: [
      "Easy to use on-site App",
      "Web portal for Management Team",
      "Unlimited use of all features including SWMS",
      "No limit on number of jobs or projects",
      "Local phone and onsite support",
    ],
    marketingIncludesFrom: "Complete Construction Pack",
    marketingButtonTone: "accent",
  },
};

export const marketingOrganisationContent: Record<
  string,
  MarketingOrganisationContent
> = {
  "1-base": {
    imageUrl: `${marketingPricingAssetBase}/pricing-owner-operator.jpg`,
    marketingTitle: "Owner Operator",
    marketingOriginalPrice: "$9.95",
  },
  "2-base": {
    imageUrl: `${marketingPricingAssetBase}/pricing-growing-business.png`,
    marketingTitle: "Growing Business",
  },
  "3-base": {
    imageUrl: `${marketingPricingAssetBase}/pricing-multi-site.png`,
    marketingTitle: "Multi-site Team",
  },
  "4-base": {
    imageUrl: `${marketingPricingAssetBase}/pricing-large-projects.jpg`,
    marketingTitle: "Large Projects",
  },
};

export const ManageSubscriptionOrgPackageData: orgPackageDataType[] =
  orgPackageData.map((pack) => {
    const content = marketingOrganisationContent[pack.lookupKey || ""];
    if (!content) return pack;

    return {
      ...pack,
      ...content,
      imageUrl: content.imageUrl || pack.imageUrl,
    };
  });

export const marketingFeatureImages: Record<string, string> = {
  courses_inductions: `${marketingPricingAssetBase}/pricing-courses.jpg`,
  progress_reports: `${marketingPricingAssetBase}/pricing-progress.jpg`,
  pst: `${marketingPricingAssetBase}/pricing-prestart-talks.jpg`,
  pssc: `${marketingPricingAssetBase}/pricing-prestart-checks.jpg`,
  feature_audit_sharing: `${marketingPricingAssetBase}/pricing-notification.jpg`,
};

export const marketingAccountImages: Record<string, string> = {
  account_pah_additional: `${marketingPricingAssetBase}/pricing-biz-mgmt.jpg`,
  account_sah_additional: `${marketingPricingAssetBase}/pricing-staff.jpg`,
  account_nah_additional: `${marketingPricingAssetBase}/pricing-notification.jpg`,
};

export const marketingExtraImages: Record<string, string> = {
  extra_setup: `${marketingPricingAssetBase}/pricing-assisted.jpg`,
  extra_training: `${marketingPricingAssetBase}/pricing-onsite.jpg`,
};

export const getMarketingBundleTitle = (lookupKey?: string | null) =>
  lookupKey ? marketingBundleContent[lookupKey]?.marketingTitle : undefined;

export const getMarketingOrganisationTitle = (lookupKey?: string | null) =>
  lookupKey
    ? marketingOrganisationContent[lookupKey]?.marketingTitle
    : undefined;

export interface PickPackDto {
  id: number;
  isSelected: boolean;
  title: string;
  description: string;
  isFree: boolean;
  lookupKey: string;
  included: boolean;
  imageUrl: string;
  list: Array<{
    para: string;
    list: Array<{ para: string; list: Array<{ para: string }> }>;
  }>;
}

const toPickPackList = (items: string[]): PickPackDto["list"] =>
  items.map((para) => {
    const [title, detail] = para.split(/:\s(.+)/);
    return detail
      ? { para: title, list: [{ para: detail, list: [] }] }
      : { para, list: [] };
  });

const applyMarketingBundleContent = (pack: PickPackDto): PickPackDto => {
  const content = marketingBundleContent[pack.lookupKey];
  if (!content) return pack;

  return {
    ...pack,
    title: content.marketingTitle || pack.title,
    description:
      content.marketingTagline ||
      content.marketingSubtitle ||
      pack.description,
    imageUrl: content.imageUrl || pack.imageUrl,
    list: [
      ...(content.marketingIncludesFrom
        ? [
            {
              para: `Everything in ${content.marketingIncludesFrom} plus:`,
              list: [],
            },
          ]
        : []),
      ...(content.marketingBullets?.length
        ? toPickPackList(content.marketingBullets)
        : pack.list),
    ],
  };
};

const swmsOnlyPickPack: PickPackDto = {
  id: 0,
  isSelected: false,
  title: "SWMS Only",
  description: "The easiest way to get site-ready includes:",
  imageUrl: imageUtils.induction,
  isFree: false,
  lookupKey: "swms_only",
  included: false,
  list: [
    {
      para: "Unlimited SWMS",
      list: [
        {
          para: "Build site-specific SWMS possible in 90 seconds.",
          list: [],
        },
      ],
    },
    {
      para: "Digital Sign-Off",
      list: [
        {
          para: "Instant on-site signing for your crew and contractors right from their phones.",
          list: [],
        },
      ],
    },
    {
      para: "Instant Sharing",
      list: [
        {
          para: "One-click sending to clients to prove you're site-ready.",
          list: [],
        },
      ],
    },
  ],
};

export const PickPackList: PickPackDto[] = [
  {
    id: 1,
    isSelected: false,
    title: "Safework essentials pack",
    description: 'Safety ‘essentials’ suitable for any industry which includes:',
    imageUrl: imageUtils.businessEssentials,
    isFree: false,
    lookupKey: "safework_essentials",
    included: false,
    list: [
      {
        para: "Hazard & Risk Reporting",
        list: [
          {
            para: "Instantly log risks or incidents with GPS-stamped records.",
            list: [],
          },
        ],
      },
      {
        para: "Incident Response",
        list: [
          {
            para: 'Assign "Make Safe" or "Repair Works" to staff immediately.',
            list: [],
          },
        ],
      },
      {
        para: "Incident Reports and Investigations",
        list: [
          {
            para: "Guided workflows ensuring you capture the right details to document and investigate incidents to protect your business.",
            list: [],
          },
        ],
      },
      {
        para: "Asset & Property Protection",
        list: [
          {
            para: "Track and manage safety risks related to your physical assets.",
            list: [],
          },
        ],
      },
      {
        para: "Custom Safety Audits",
        list: [
          {
            para: "Create audits to guide your team through any task or process.",
            list: [],
          },
        ],
      },
      {
        para: "Digital Policy Hub",
        list: [
          {
            para: "Share company policies, procedures, and site docs with any team member.",
            list: [],
          },
        ],
      },
      {
        para: "Mandatory Read Receipts",
        list: [
          {
            para: "Require digital signatures on critical documents to prove compliance.",
            list: [],
          },
        ],
      },
      {
        para: "Offline Mode",
        list: [
          {
            para: "Access features without signal, the data saves and auto-syncs when you reconnect.",
            list: [],
          },
        ],
      },
    ],
  },
  {
    id: 2,
    isSelected: false,
    title: "SWMS pack",
    description: 'Compliance for the construction industry includes:',
    isFree: false,
    lookupKey: "complete_construction",
    included: false,
    imageUrl: imageUtils.induction,
    list: [
      {
        para: "Includes everything in the Safework essentials pack",
        list: [],
      },
      {
        para: "Unlimited Site-Specific SWMS",
        list: [
          {
            para: "Build, sign, and send in minutes.",
            list: [],
          },
        ],
      },
      {
        para: "Unlimited safety management plans",
        list: [
          {
            para: "Compliance for larger projects.",
            list: [],
          },
        ],
      },
      {
        para: "Hazard & Risk Reporting",
        list: [
          {
            para: "Instantly log risks or incidents with GPS-stamped records.",
            list: [],
          },
        ],
      },
      {
        para: "Asset Maintenance and Incident Response",
        list: [
          {
            para: 'Assign "Make Safe" or "Repair Works" to staff immediately.',
            list: [],
          },
        ],
      },
      {
        para: "Toolbox Talks (Pre-Starts)",
        list: [
          {
            para: "Record, log and share your team briefings so no one misses the important details.",
            list: [],
          },
        ],
      },
      {
        para: "Digital Site Documents",
        list: [
          {
            para: "Keep your insurance, licences, and important documents in your pocket and always ready.",
            list: [],
          },
        ],
      },
      {
        para: "Digital Policy Hub",
        list: [
          {
            para: "Share company policies, procedures, and site docs with any team member.",
            list: [],
          },
        ],
      },
      {
        para: "Custom Safety Audits",
        list: [
          {
            para: "Create audits to guide your team through any task or process.",
            list: [],
          },
        ],
      },
      {
        para: "Offline Mode",
        list: [
          {
            para: "Access features without signal, the data saves and auto-syncs when you reconnect.",
            list: [],
          },
        ],
      },
      {
        para: "Incident Reports and Investigations",
        list: [
          {
            para: "Guided workflows ensuring you capture the right details to document and investigate incidents to protect your business.",
            list: [],
          },
        ],
      },
      {
        para: "Courses & Inductions",
        list: [
          {
            para: "Seamless management and delivery of training modules, inductions, and certification programs, ensuring compliance with safety standards.",
            list: [],
          },
        ],
      },
    ],
  },
  {
    id: 3,
    isSelected: false,
    title: "Business tools pack",
    description: 'This is our complete plan and includes everything in our SWMS and safety compliance “safe site” pack and adds our powerful business tools, including: ',
    imageUrl: imageUtils.workAssessment,
    isFree: false,
    lookupKey: "sb_pro_pack",
    included: false,
    list: [
      {
        para: "Includes everything in the SWMS pack",
        list: [],
      },
      {
        para: "Staff management",
        list: [
          {
            para: "Create schedules and use time/GPS stamped records to track staff hours accurately.",
            list: [],
          },
        ],
      },
      {
        para: "Client Job Records",
        list: [
          {
            para: "Keep client details linked to specific jobs for easy access in the field or office.",
            list: [],
          },
        ],
      },
      {
        para: "Contractor Management",
        list: [
          {
            para: "Manage assignments and contact contractors directly through the scheduling tools.",
            list: [],
          },
        ],
      },
      {
        para: "Digital Plans & Scopes",
        list: [
          {
            para: "Share architectural drawings, engineering plans, and SOWs with staff and contractors instantly.",
            list: [],
          },
        ],
      },
      {
        para: "Staff Ticket Wallet",
        list: [
          {
            para: "Store staff qualifications and tickets so they are ready for inspection anywhere.",
            list: [],
          },
        ],
      },
      {
        para: "Invoice Auditing",
        list: [
          {
            para: "Track contractor site attendance and audit invoices against their actual times on site.",
            list: [],
          },
        ],
      },
      {
        para: "Pre-Start Safety Checks",
        list: [
          {
            para: "Digital checklists to suit your needs.",
            list: [],
          },
        ],
      },
      {
        para: "Job Progress Reports",
        list: [
          {
            para: "Monitor and send professional updates to clients.",
            list: [],
          },
        ],
      },
      {
        para: "Variations and Defects Management",
        list: [],
      },
      {
        para: "Courses & Inductions",
        list: [
          {
            para: "Seamless management and delivery of training modules, inductions, and certification programs, ensuring compliance with safety standards.",
            list: [],
          },
        ],
      },
      {
        para: "Work Variations",
        list: [
          {
            para: "Raise scope changes on the spot and capture instant approval to ensure you get paid for every extra.",
            list: [],
          },
        ],
      },
      {
        para: "Work Defects",
        list: [
          {
            para: "Log defects and capture instant acknowledgement to track resolution and ensure quality control.",
            list: [],
          },
        ],
      },
      {
        para: "Audit Tool Sharing",
        list: [
          {
            para: "Share any form via email, website, or QR code on site signage to let anyone complete audits or reports instantly.",
            list: [],
          },
        ],
      },
    ],
  },
];

export const MarketingPickPackList: PickPackDto[] = [
  swmsOnlyPickPack,
  ...PickPackList,
];

export const ManageSubscriptionPickPackList: PickPackDto[] =
  MarketingPickPackList.map(applyMarketingBundleContent);

export const grandfatheredBundleTitles: Record<string, string> = {
  safe_work_pack: "Safe Work Pack",
  swms_safe_work_pack: "SWMS Safe Work Pack",
  safety_compliance_bm_pack: "Safety Compliance & Business Management Pack",
  business_essentials: "Business Essentials",
  information_induction: "Information and Induction",
  work_assessment: "Work Assessment",
  custom_swms_jsa: "Custom SWMS and JSA",
  hazard_management: "Hazard Management",
  safety_compliance: "Safety and Compliance",
  audit_tool: "Audit Tool",
  financial_tools: "Financial Tools",
};

export const getPickPackDisplayTitle = (lookupKey: string): string =>
  ManageSubscriptionPickPackList.find((pack) => pack.lookupKey === lookupKey)
    ?.title ||
  grandfatheredBundleTitles[lookupKey] ||
  lookupKey;

export interface AddOnListDto {
  id: number;
  isSelected: boolean;
  title: string;
  description: string;
  lookupKey: string;
  included: boolean;
  list: Array<{
    para: string;
    list: Array<{ para: string; list: Array<{ para: string }> }>;
  }>;

  imageUrl: string;
}

export const AddOnList: AddOnListDto[] = [
  {
    id: 1,
    isSelected: false,
    included: false,
    title: "Courses & Inductions",
    lookupKey: "courses_inductions",
    description:
      "Seamless management and delivery of training modules, inductions, and certification programs, ensuring efficient tracking of employee progress and compliance with safety standards.",
    list: [
      {
        para: "Access to an existing database of Courses/Inductions",
        list: [],
      },
      { para: "Port existing course material to insite.work", list: [] },
    ],
    imageUrl: imageUtils.induction,
  },
  {
    id: 2,
    isSelected: false,
    included: false,
    title: "Progress Reports",
    lookupKey: "progress_reports",
    description:
      "Comprehensive tracking and analysis tools for monitoring project advancement, enabling stakeholders to review and assess ongoing developments.",
    list: [
      { para: "Complete onsite Progress Reports", list: [] },
      { para: "Review completed reports via web portal", list: [] },
    ],
    imageUrl: imageUtils.accountSetup,
  },
  {
    id: 3,
    isSelected: false,
    included: false,
    title: "Pre-Start Talks",
    lookupKey: "pst",
    description:
      "Prepare, schedule, and document structured safety discussions, promoting proactive communication and risk awareness among teams.",
    list: [
      { para: "Lead Pre-Start Talks Onsite", list: [] },
      { para: "Record Attendance at Pre-Start Talk", list: [] },
      { para: "Listen to Previously Recorded Talks", list: [] },
      { para: "Review Completed Talks via Web Portal", list: [] },
    ],
    imageUrl: imageUtils.pahAcc,
  },
  {
    id: 4,
    isSelected: false,
    included: false,
    title: "Pre-Start Safety Checks",
    lookupKey: "pssc",
    description:
      "Systematic and efficient assessment of safety measures and procedures before work commences, ensuring a proactive approach to hazard identification and prevention.",
    list: [
      { para: "Complete onsite Pre-Start Safety Checks", list: [] },
      { para: "Review completed Safety Checks via web portal", list: [] },
    ],
    imageUrl: imageUtils.smallBusiness2,
  },
  /* {
    id: 6,
    isSelected: false,
    included: false,
    title: "Work Variations",
    lookupKey: "feature_variations",
    description:
      "Seamless documentation, management, and tracking of changes in project scope or requirements, ensuring transparent communication and effective handling of modifications.",
    list: [
      { para: "Raise Work Variations onsite or via the web portal", list: [] },
      { para: "Request and record approval of Work Variations", list: [] },
    ],
    imageUrl: imageUtils.enterprise,
  }, */
  /*  {
    id: 7,
    isSelected: false,
    included: false,
    title: "Work Defects",
    lookupKey: "feature_defects",
    description:
      "Document and manage defects, allowing for efficient tracking, resolution, and reporting to ensure quality control and compliance.",
    list: [
      { para: "Record Defects onsite or via the web portal", list: [] },
      { para: "Request and record acknowledgement of Defects", list: [] },
    ],
    imageUrl: imageUtils.accountSetup,
  }, */
  /* {
    id: 8,
    isSelected: false,
    included: false,
    title: "Incident Reports",
    lookupKey: "feature_incidents",
    description:
      "Timely recording of incident reports, fostering a proactive approach to safety management and facilitating detailed analysis of incidents.",
    list: [
      { para: "Complete onsite Incident Reports", list: [] },
      { para: "Review completed reports via web portal", list: [] },
    ],
    imageUrl: imageUtils.msRw,
  }, */
  /*  {
    id: 9,
    isSelected: false,
    included: false,
    title: "Site Documents & Information",
    lookupKey: "feature_documents",
    description:
      "Centralise essential documents, manuals, and information, ensuring easy access, version control, and streamlined dissemination across the workforce.",
    list: [
      { para: "Upload documents and files", list: [] },
      { para: "Send, Sign and Review Documents", list: [] },
      { para: "Generate Weblinks for non insite.work users to sign", list: [] },
    ],
    imageUrl: imageUtils.onsiteTraining,
  }, */
  /*  {
    id: 10,
    isSelected: false,
    included: false,
    title: "SWMS Generation & Management",
    lookupKey: "feature_swms",
    description:
      "Enables the creation and management of Safe Work Method Statements (SWMS), facilitating customisation, storage, and retrieval of tailored safety procedures.",
    list: [
      { para: "Generate Safe Work Method Statements (SWMS)", list: [] },
      { para: "Send, Sign and Review generated SWMS", list: [] },
      {
        para: "Customise Trades, Work Activities & Work Activity Groups",
        list: [],
      },
    ],
    imageUrl: imageUtils.swmsJsa,
  }, */
  /* {
    id: 11,
    isSelected: false,
    included: false,
    title: "JSA Reports",
    lookupKey: "feature_jsa",
    description:
      "Enables the creation and management of Job Safety Analysis (JSA) reports, providing tools for detailed risk assessment, mitigation strategies, and documentation.",
    list: [
      { para: "Generate Job Safety Analysis (JSA) reports", list: [] },
      { para: "Sign JSAs", list: [] },
      { para: "Share JSAs", list: [] },
    ],
    imageUrl: imageUtils.enterprise,
  }, */
  /* {
    id: 12,
    isSelected: false,
    included: false,
    title: "Audit Tool Sharing",
    lookupKey: "feature_audit_sharing",
    description: "",
    list: [
      {
        para: "Audit Sharing",
        list: [
          {
            para: "Share Custom Audits or forms with the anyone, examples include incident forms, hazard reports or customer surveys.",
            list: [],
          },
          {
            para: "Share through weblinks or QR codes which you can post on your website, on a sign at your workplace or share through email.",
            list: [],
          },
        ],
      },
    ],
    imageUrl: imageUtils.auditSharing,
  }, */
];

export declare type staffCardListDto = {
  id: number;
  title: string;
  lookupKey: string;
  count: number;
  descriptions: string[];
  list: string[];
  imageUrl: string;
};

export const StaffCardList: Array<staffCardListDto> = [
  {
    id: 1,
    title: "Business Management Accounts",
    lookupKey: "account_pah_additional",
    count: 0,
    descriptions: [
      "Manager/Administrator accounts allow users to set up and remotely manage team members, contractors and jobs.",
      "Through these accounts users can view progress and manage safety on multiple sites.",
      "Work with the full suite of insite,work tools anywhere with a net connection.",
    ],
    list: [
      "Web Portal Access & Admin Level Capabilities",
      "Mobile App Access (iOS & Android)",
    ],
    imageUrl: imageUtils.pahAcc,
  },
  {
    id: 2,
    title: "Staff Accounts",
    lookupKey: "account_sah_additional",
    count: 0,
    descriptions: [
      "Access to essential tools and that support compliance, remote reporting and safety.",
      "Give your team the tools that they actually want to use: easy to navigate with low on-boarding costs.",
    ],
    list: ["Limited Web Portal Access", "Mobile App Access (iOS & Android)"],
    imageUrl: imageUtils.sahAcc,
  },
  {
    id: 3,
    title: "Notification Accounts",
    lookupKey: "account_nah_additional",
    count: 0,
    descriptions: [
      "Every member of your team becomes part of the safety network.",
      "A tool that supports easy reporting of hazards or asset issues.",
      "Ease of use creates high uptake levels and this means safer workplaces.",
    ],
    list: ["Limited Web Portal Access", "Mobile App Access (iOS & Android)"],
    imageUrl: imageUtils.nahAcc,
  },
];

export const addExtrasList: Array<staffCardListDto> = [
  {
    id: 1,
    title: "Assisted Account Setup",
    lookupKey: "extra_setup",
    count: 0,
    descriptions: [
      "If you need help to rapidly set up your system, no mater how large or small your enterprise, we can supply professional help.",
    ],
    list: [
      "Staff account setup",
      "Data input service",
      "Remote Customer Service Help",
    ],
    imageUrl: imageUtils.accountSetup,
  },
  {
    id: 2,
    title: "Onsite Training",
    lookupKey: "extra_training",
    count: 0,
    descriptions: [
      "Software setup and team training both onsite and through Zoom. Our experienced team can have you set up and trained in a matter of hours.",
    ],
    list: [
      "Onsite and/or Remote Training Staff",
      "Technical Software Assistance",
    ],

    imageUrl: imageUtils.onsiteTraining,
  },
];
